import { transformPermissionsToRoles } from 'src/utils/general';
import type { Role } from 'src/services/user';

/**
 * Pure logic behind the "add user" wizard. Deliberately free of React so the
 * permission mapping, the summary line and the level constraints can be tested
 * without rendering anything.
 */

/** The three levels the backend knows. There are no res.groups beyond these. */
export type PermissionLevel = 'none' | 'read' | 'read_write';

export type PermissionAreaKey = 'outbound' | 'products' | 'returns' | 'carrier' | 'integrations' | 'cases';

export type PermissionLevels = Record<PermissionAreaKey, PermissionLevel>;

export const PERMISSION_AREA_KEYS: PermissionAreaKey[] = [
  'outbound',
  'products',
  'returns',
  'carrier',
  'integrations',
  'cases',
];

/**
 * Areas that cannot be revoked entirely. `cases` keeps a read floor because the
 * portal's support flow is unusable without it — same rule the previous form had.
 */
export const PERMISSION_LEVEL_FLOOR: Partial<Record<PermissionAreaKey, PermissionLevel>> = {
  cases: 'read',
};

export const DEFAULT_PERMISSION_LEVELS: PermissionLevels = {
  outbound: 'none',
  products: 'none',
  returns: 'none',
  carrier: 'none',
  integrations: 'none',
  cases: 'read',
};

/** Levels selectable for an area, honouring its floor. */
export const getSelectableLevels = (area: PermissionAreaKey): PermissionLevel[] => {
  const floor = PERMISSION_LEVEL_FLOOR[area];
  if (floor === 'read') return ['read', 'read_write'];
  if (floor === 'read_write') return ['read_write'];
  return ['none', 'read', 'read_write'];
};

/** Every area at its highest level — the "grant full access everywhere" shortcut. */
export const grantFullAccessToAllAreas = (): PermissionLevels =>
  PERMISSION_AREA_KEYS.reduce((levels, area) => ({ ...levels, [area]: 'read_write' }), {} as PermissionLevels);

export const hasFullAccessEverywhere = (levels: PermissionLevels): boolean =>
  PERMISSION_AREA_KEYS.every(area => levels[area] === 'read_write');

export const getGrantedAreas = (levels: PermissionLevels): PermissionAreaKey[] =>
  PERMISSION_AREA_KEYS.filter(area => levels[area] !== 'none');

/**
 * The `(view, admin)` boolean pair shape the shared role transform expects.
 * Keeping this conversion separate means `transformPermissionsToRoles` — and its
 * existing tests — stay untouched while the UI works with a single level per area.
 */
export const permissionLevelsToFlags = (levels: PermissionLevels): Record<string, boolean> =>
  PERMISSION_AREA_KEYS.reduce<Record<string, boolean>>((flags, area) => {
    const level = levels[area];
    return {
      ...flags,
      [`${area}_view`]: level === 'read' || level === 'read_write',
      [`${area}_admin`]: level === 'read_write',
    };
  }, {});

/** Roles payload for POST /users/user/. */
export const permissionLevelsToRoles = (t: (key: string) => string, levels: PermissionLevels): Role[] =>
  transformPermissionsToRoles(t, permissionLevelsToFlags(levels));

export const PERMISSION_LEVEL_LABEL_KEYS: Record<PermissionLevel, string> = {
  none: 'general.permission_none',
  read: 'general.view_only',
  read_write: 'general.permission_full',
};

export const PERMISSION_AREA_LABEL_KEYS: Record<PermissionAreaKey, string> = {
  outbound: 'navbar.outbound',
  products: 'returns.products',
  returns: 'navbar.returns',
  carrier: 'general.carrier',
  integrations: 'navbar.integrations',
  cases: 'navbar.cases',
};

export const PERMISSION_AREA_HINT_KEYS: Record<PermissionAreaKey, string> = {
  outbound: 'general.user_wizard.hint_outbound',
  products: 'general.user_wizard.hint_products',
  returns: 'general.user_wizard.hint_returns',
  carrier: 'general.user_wizard.hint_carrier',
  integrations: 'general.user_wizard.hint_integrations',
  cases: 'general.user_wizard.hint_cases',
};

/**
 * One-line recap of the granted rights for the summary card in step 2, e.g.
 * "Auslieferungen: Lesen · Cases: Vollzugriff".
 */
export const buildPermissionSummary = (t: (key: string) => string, levels: PermissionLevels): string => {
  const granted = getGrantedAreas(levels);
  if (granted.length === 0) return t('general.user_wizard.summary_no_access');
  if (hasFullAccessEverywhere(levels)) return t('general.user_wizard.summary_full_access');
  return granted
    .map(area => `${t(PERMISSION_AREA_LABEL_KEYS[area])}: ${t(PERMISSION_LEVEL_LABEL_KEYS[levels[area]])}`)
    .join(' · ');
};

/**
 * Hint under the password strength meter, matching the current score. Score 0
 * (nothing typed yet) spells out the full policy — see getPasswordStrength for
 * how the four policy criteria are folded onto three segments.
 */
export const PASSWORD_HINT_KEYS: Record<0 | 1 | 2 | 3, string> = {
  0: 'general.user_wizard.password_requirements',
  1: 'general.user_wizard.password_weak',
  2: 'general.user_wizard.password_medium',
  3: 'general.user_wizard.password_strong',
};
